import {inject, Injectable, NgZone} from "@angular/core";
import {ViewPortManagerStore, ViewPortWrapper} from "../store/view-port-manager.store";
import {RendererStore} from "../store/Renderer.store";
import { CameraService } from "./camera.service";

export enum RenderingStrategy {
    'animate' = 'animate',
    'cycled' = 'cycled',
    'static' = 'static'
}

/**
 * Tarjoaa palvelut renderöintiin
 */
@Injectable({providedIn: 'root'})
export class RenderingService {
    private readonly viewPortManager = inject(ViewPortManagerStore);
    private readonly rendererStore: RendererStore = inject(RendererStore);
    private readonly cameraService = inject(CameraService);
    private readonly ngZone = inject(NgZone);

    private lastFrameTime: number = performance.now();
    private frameCount: number = 0;
    private fps: number = 0;

    // Sidotaan animaatiometodi kerran
    private readonly boundAnimate: () => void = this.animate.bind(this);


    async startAnimate(cycled: boolean = false): Promise<void> {
        if (!this.shouldStartAnimating(cycled)) { return; }
        this.initializeAnimation(cycled);
        this.animate();
    }

    async animateOnce(){
        this.canRender();
        this.rendererStore.animatingStatus(true, false);
        this.animate();
        this.rendererStore.animatingStatus(false);
    }

    private shouldStartAnimating(cycled: boolean): boolean {
        if (!this.rendererStore.animating) {
            return true;
        }
        if (cycled) {
            this.rendererStore.resetAnimationCycle(Date.now());
        }
        return false;
    }

    private initializeAnimation(cycled: boolean): void {
        this.canRender();
        this.rendererStore.animatingStatus(true, cycled);
    }

    public stopAnimate(): void {
        this.rendererStore.animatingStatus(false);
    }

    private animate(): void {
        this.ngZone.runOutsideAngular(() => {
            const frame = requestAnimationFrame(this.boundAnimate);
            this.checkAnimateValidity(frame);
            if(!this.rendererStore.animating) { return; }

            // Cache: Haetaan canvasin DOM-ominaisuudet vain kerran per frame
            const canvasRect = this.rendererStore.canvas.getBoundingClientRect();
            // Käydään läpi kaikki viewPortit
            this.viewPortManager.getAll().forEach(vp => {
                if(!vp.isVisible){ return ;}

                const vpRect = vp.viewPort.getBoundingClientRect();
                this.render(vp, vpRect, canvasRect);
            });
        });

        // FPS-laskenta
        /*const now = performance.now();
        this.frameCount++;
        if (now - this.lastFrameTime >= 1000) {
            this.fps = this.frameCount;
            this.frameCount = 0;
            this.lastFrameTime = now;
            console.log(`FPS: ${this.fps}`); // Tulostetaan FPS konsoliin
        }*/
    }

    private render(vp: ViewPortWrapper, vpRect: DOMRect, canvasRect: DOMRect){

        //No need to render if outside of UI
        if (this.isOutsideUI(vpRect, canvasRect)) { return; }

        //Position renderable area
        this.handleViewPortArea(vp.viewPort, canvasRect);

        //Set camera view aspect and offset
        this.handleCamera(vp);

        //this.rendererStore.renderer.render(vp.scene, vp.camera);
        this.rendererStore.render(vp.scene, vp.camera);

        this.handleControls(vp);
    }

    private isOutsideUI(vpRect: DOMRect, canvasRect: DOMRect): boolean {
        return vpRect.top > canvasRect.bottom ||
            vpRect.left < canvasRect.left ||
            vpRect.right > canvasRect.right ||
            vpRect.bottom < canvasRect.top;
    }

    private handleViewPortArea(vpElem: HTMLElement, canvasRect: DOMRect): void {
        if (!vpElem) { throw new Error('ViewPort element is not defined'); }

        const renderer = this.rendererStore.renderer;
        const vpRect = vpElem.getBoundingClientRect();

        // Laske viewportin sijainti suhteessa canvasiin:
        const offsetX = vpRect.left - canvasRect.left;
        // Huom: WebGL-origo on vasen alakulma, DOM-origo vasen yläkulma
        const offsetY = canvasRect.height - (vpRect.bottom - canvasRect.top);

        // Aseta viewport ja scissor oikeilla arvoilla:
        renderer.setViewport(offsetX, offsetY, vpRect.width, vpRect.height);
        renderer.setScissor(offsetX, offsetY+60, vpRect.width, vpRect.height-60);
    }

    private handleCamera(vp: ViewPortWrapper): void {
        this.cameraService.updateViewportCamera(vp.camera, vp.viewPort, this.rendererStore.canvas);
    }

    private handleControls(vp: ViewPortWrapper): void {
        if (!vp?.controls)  { return; }
        vp.controls.update();
    }

    // Prevents OrbitControl asymptotic movement
    private stopMovingWhenAlmostStill(vp: ViewPortWrapper): void {
        // Valitse sopiva kynnysarvo
        const deltaThreshold = 0.0001;

        // Alustetaan edelliset arvot ensimmäisellä kierroksella
        if (!vp.previousControlState) {
            vp.previousControlState = {
                target: vp.controls.target.clone(),
                azimuthalAngle: vp.controls.getAzimuthalAngle(),
                polarAngle: vp.controls.getPolarAngle(),
            };
        }

        // Laske kontrollien sisäisten arvojen muutos
        const currentTarget = vp.controls.target;
        const currentAzimuthalAngle = vp.controls.getAzimuthalAngle();
        const currentPolarAngle = vp.controls.getPolarAngle();

        const targetDelta = currentTarget.clone().sub(vp.previousControlState.target).length();
        const azimuthalDelta = Math.abs(currentAzimuthalAngle - vp.previousControlState.azimuthalAngle);
        const polarDelta = Math.abs(currentPolarAngle - vp.previousControlState.polarAngle);

        const totalDelta = targetDelta + azimuthalDelta + polarDelta;

        // Jos liike on tarpeeksi hidasta, pysäytä damping kokonaan
        if (totalDelta < deltaThreshold) {
            vp.controls.enableDamping = false;
        } else {
            vp.controls.enableDamping = true;
        }

        // Päivitä ohjaimet normaalisti
        vp.controls.update();

        // Tallenna nykyinen tila seuraavaa framea varten
        vp.previousControlState.target.copy(currentTarget);
        vp.previousControlState.azimuthalAngle = currentAzimuthalAngle;
        vp.previousControlState.polarAngle = currentPolarAngle;
    }


    private checkAnimateValidity(frame: number): void {
        const { animateNecessary, canNotRender, animating, overCycleTime } = this.rendererStore;

        //viewports contain animate strategy
        if (animateNecessary && !canNotRender) {

            //cycle time is over
            if (overCycleTime) { this.rendererStore.resetAnimationCycle(); }

            if (!animating) { cancelAnimationFrame(frame); }

            return;
        }

        if (canNotRender || !animating || overCycleTime) {
            cancelAnimationFrame(frame);
            this.rendererStore.animatingStatus(false);
            this.rendererStore.resetAnimationCycle();
        }
    }

    clearViewPort(id: string): void {
        const vp = this.viewPortManager.getById(id);
        const { canvas, renderer } = this.rendererStore;
        if(!vp) { throw new Error(`ViewPort with id ${id} not found`); }
        if (!canvas || !renderer) { throw new Error('Canvas or renderer not initialized'); }
        this.handleViewPortArea(vp.viewPort, canvas.getBoundingClientRect());
        this.rendererStore.clearCanvas();
    }

    private canRender(): void {
        if( this.rendererStore.canNotRender ) { throw new Error('Renderer or canvas is not initialized'); }
    }

}